import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';
import { currentNavEntry, withDividers, type NavEntry, type NavRow as NavRowEntry } from '@/lib/navEntries';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

/** The main menu: one row of pages, or one button where the row does not fit. */
export function MainMenu({ entries, collapsed }: { entries: NavEntry[]; collapsed: boolean }) {
  return collapsed ? <CollapsedNav entries={entries} /> : <NavRow entries={entries} />;
}

function NavRow({ entries }: { entries: NavEntry[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const fullRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(true);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const full = fullRef.current;
    if (!box || !full || typeof ResizeObserver === 'undefined') return;
    const check = () => setFits(full.scrollWidth <= box.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(box);
    observer.observe(full);
    return () => observer.disconnect();
    // The row and the button draw different boxes; watch the ones on screen.
  }, [entries, fits]);

  const rows = withDividers(entries);
  if (!fits) {
    return (
      <>
        <div className="container mx-auto">
          <div ref={boxRef} className="relative h-0 overflow-hidden">
            <NavRowMeasure ref={fullRef} rows={rows} />
          </div>
        </div>
        <CollapsedNav entries={entries} />
      </>
    );
  }
  return (
    <nav className="bg-tres-menu-bg text-white/90">
      <div className="container mx-auto">
        <div ref={boxRef} className="relative flex h-10 items-center gap-1 whitespace-nowrap text-sm">
          <NavRowMeasure ref={fullRef} rows={rows} />
          {rows.map((row, i) =>
            'divider' in row ? (
              <NavDivider key={`d${i}`} />
            ) : (
              <NavLinkItem key={row.to} to={row.to}>
                {row.label}
              </NavLinkItem>
            ),
          )}
        </div>
      </div>
    </nav>
  );
}

/** The full row, drawn out of sight, so its width is known whether or not it is shown. */
function NavRowMeasure({ ref, rows }: { ref: React.Ref<HTMLDivElement>; rows: NavRowEntry[] }) {
  return (
    <div
      ref={ref}
      aria-hidden
      data-nav-measure
      className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-1 whitespace-nowrap text-sm"
    >
      {rows.map((row, i) =>
        'divider' in row ? (
          <NavDivider key={`d${i}`} />
        ) : (
          <span key={row.to} className={NAV_ITEM}>
            {row.label}
          </span>
        ),
      )}
    </div>
  );
}

function CollapsedNav({ entries }: { entries: NavEntry[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Navigating closes the menu, whichever way the route changed.
  useEffect(() => setOpen(false), [pathname]);
  const current = currentNavEntry(entries, pathname);

  return (
    <nav className="bg-tres-menu-bg text-white/90">
      <div className="container mx-auto text-sm">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="main-menu"
          className={cn(buttonVariants({ variant: 'chrome', size: 'md' }), 'flex h-10 w-full items-center gap-3 text-left border-0')}
        >
          {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          <span className="font-medium">{current?.label ?? 'Menu'}</span>
        </button>
        {open && (
          <div id="main-menu" className="flex flex-col pb-2">
            {withDividers(entries).map((row, i) =>
              'divider' in row ? (
                <span key={`d${i}`} aria-hidden className="my-1 h-px bg-white/20" />
              ) : (
                <NavLinkItem key={row.to} to={row.to}>
                  {row.label}
                </NavLinkItem>
              ),
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

const NAV_ITEM = 'inline-flex h-10 items-center px-3 xl:px-4';

function NavDivider() {
  return <span aria-hidden className="mx-2 h-4 w-px shrink-0 bg-white/20" />;
}

function NavLinkItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          NAV_ITEM,
          'transition-colors',
          isActive
            ? 'bg-white/10 text-white'
            : 'text-white/75 hover:bg-white/10 hover:text-white',
        )
      }
    >
      {children}
    </NavLink>
  );
}
